import { useDebounceCallback } from "@react-hook/debounce";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import type {
  CurrentBrandData,
  CurrentUserData,
} from "lib/auth/currentUserHooks";

let segmentBrandContext: {
  brandId: string;
  brandName: string;
} | null = null;
type SegmentTrackCallback = () => void;
type SegmentTrackProperties = { [property: string]: unknown };

export function getIsSegmentEnabled() {
  return process.env.NODE_ENV === "production";
}

function getSegmentPropertiesWithBrandContext(
  properties?: SegmentTrackProperties,
) {
  if (!segmentBrandContext) return properties;

  return {
    ...segmentBrandContext,
    ...properties,
  };
}

export function setSegmentBrandContext(
  brandData: Pick<CurrentBrandData, "id" | "name"> | null | undefined,
) {
  segmentBrandContext = brandData
    ? { brandId: brandData.id, brandName: brandData.name }
    : null;
}

export function useSegmentTrackPage() {
  const router = useRouter();
  const { isReady, asPath } = router;

  const previousAsPath = useRef<string>();

  const trackPage = useDebounceCallback(
    (properties: { [property: string]: unknown }) => {
      if (typeof window.analytics !== "undefined" && getIsSegmentEnabled()) {
        window.analytics.page(getSegmentPropertiesWithBrandContext(properties));
      }
    },
    250,
  );

  useEffect(() => {
    if (isReady) {
      if (previousAsPath.current) {
        trackPage({
          referrer: `${window.location.origin || ""}${previousAsPath.current}`,
        });
      }
      previousAsPath.current = asPath;
    }
  }, [trackPage, isReady, asPath]);
}

let lastSegmentUserId: string | undefined;

export function identifySegmentUser(
  userData:
    | Partial<
        Pick<
          CurrentUserData,
          "id" | "email" | "firstName" | "lastName" | "createdAt" | "isStaff"
        >
      >
    | null
    | undefined,
): void {
  const { id, email, firstName, lastName, createdAt, isStaff } = userData || {};

  if (
    !getIsSegmentEnabled() ||
    typeof window === "undefined" ||
    typeof window.analytics === "undefined" ||
    !id ||
    // Avoid sending duplicative `identify` events
    lastSegmentUserId === id
  ) {
    return;
  }

  window.analytics.identify(id, {
    createdAt,
    email,
    firstName,
    lastName,
    isStaff,
  });

  lastSegmentUserId = id;
}

export const trackEvent: typeof window.analytics.track = (
  event: string,
  properties?: SegmentTrackProperties | SegmentTrackCallback,
  options?: SegmentAnalytics.SegmentOpts | SegmentTrackCallback,
  callback?: SegmentTrackCallback,
) => {
  if (!getIsSegmentEnabled()) return;

  if (typeof window === "undefined" || !window.analytics) {
    // eslint-disable-next-line no-console
    console.warn(`Unable to track event: ${event}`);
    return;
  }

  if (typeof properties === "function") {
    return window.analytics.track(
      event,
      getSegmentPropertiesWithBrandContext(),
      undefined,
      properties,
    );
  }

  if (typeof options === "function") {
    return window.analytics.track(
      event,
      getSegmentPropertiesWithBrandContext(properties),
      options,
    );
  }

  return window.analytics.track(
    event,
    getSegmentPropertiesWithBrandContext(properties),
    options,
    callback,
  );
};
